import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';

import { 
  AppProvider, Page, Card, 
  Tooltip, Icon, DisplayText 
} from '@shopify/polaris';
import { QuestionMarkMajorMonotone } from '@shopify/polaris-icons';

import translations from '@shopify/polaris/locales/es.json';
import AutocompleteSearch from "@presentational/AutocompleteSearch";
import SaleReportTransfersBrief from './SaleReportTransfersBrief';
import TransactionsHistory from '@container/sale_report/components/SaleReportTransactionsHistory';

import * as shapes from 'utils/shapeProps';
import join from 'lodash/join';

const propTypes = {
  transactionsReport: PropTypes.shape({
    user_ids: PropTypes.arrayOf(PropTypes.number).isRequired,
    report_items: PropTypes.arrayOf(
      PropTypes.shape({
        user_id: PropTypes.number.isRequired,
        currency_code: PropTypes.string.isRequired,
        adjustments_origin_sale_report_ids: PropTypes.array, // IDs of sale report adjustments origins to display (nil if none)
    
        substractions: PropTypes.number, // string when coming from backend decimal
        substractions_money: PropTypes.string,
        substractions_reasons: PropTypes.arrayOf(PropTypes.string),
    
        subtotal: PropTypes.string.isRequired,
        subtotal_money: PropTypes.string.isRequired,
    
        full_amount: PropTypes.string.isRequired,
        full_amount_money: PropTypes.string.isRequired,
        returns_overprice: PropTypes.bool.isRequired,
    
        transaction_history: shapes.transactionHistoryShape.isRequired,
        transaction_totals: PropTypes.object,
        trasaction_totals_money: PropTypes.array,
    
        data: PropTypes.arrayOf(
          PropTypes.shape({
            // transfer: PropTypes.object.isRequired,
            transfer_created_at: PropTypes.string.isRequired,
            transfer_vendor: PropTypes.string.isRequired,
            transfer_currency_unit: PropTypes.string.isRequired,
            transfer_invoice_number: PropTypes.string.isRequired,
            transfer_invoice_amount_money: PropTypes.string.isRequired,
            user_id: PropTypes.number,
            amount_paid: PropTypes.string.isRequired,
            amount_paid_money: PropTypes.string,
            returns_overprice: PropTypes.bool.isRequired,
          }), // end data shape
        ),// end data array_of
      }), // end group_items shape
    ), // end report_items (by seller)
  }).isRequired,
  
  saleReport: PropTypes.object.isRequired,
  isSeller: PropTypes.bool.isRequired,
  sellerUsers: shapes.selectOptionsShape,
};

const sellerTotalColumns = [
  {  size: 4, header: I18n.t("activerecord.attributes.transfer.vendor"), accessor: "vendor" },
  {  size: 2, header: I18n.t("activerecord.attributes.general.currency"), accessor: "currency_code" },
  {  size: 4, header: I18n.t("activerecord.attributes.general.total"), accessor: "transaction_totals_money" },
]

const SaleReportShow = (props) => {
  console.log("Props", props);
  const { saleReport, transactionsReport} = props;
  const [selected, setSelected] = useState(0);
  const [focusedSellerData, setFocusedSellerData] = useState( props.isSeller ? transactionsReport.report_items[0] : null )

  const handleUserChange = (userId) => {
    const sellerReportData = transactionsReport.report_items.find( (reportItem) => reportItem.user_id == userId );
    setFocusedSellerData(sellerReportData);
  };

  const handleClearUser = useCallback(() => setFocusedSellerData(null));
  const focusedSellerTotalIsNegative = focusedSellerData && focusedSellerData.returns_overprice;

  // array of strings. If filtering only display filtered seller totals on Resumen (which have one number not an array)
  const focusedSellerSubtotal = (focusedSellerData && focusedSellerData.subtotal_money);
  const focusedSellerTotal = (focusedSellerData && focusedSellerData.full_amount_money);

  console.log("FSD", focusedSellerData);

  return(
    <AppProvider i18n={translations}>
      <Page
        fullWidth
        title={`${I18n.t("activerecord.models.sale_report.one")} ${saleReport.display_id}`}
        titleMetadata={ saleReport.date_range }
        breadcrumbs={[{content: I18n.t("actions.back"), url: '/sale_reports'}]}
      >
        <div className='boxshadow'>
          <Card>
            <Card.Header title="Resumen">
              { !!props.sellerUsers &&
                <AutocompleteSearch
                  placeholder = "Seleccionar Seller..."
                  filterOptions = { props.sellerUsers }
                  onClearCallback={ handleClearUser }
                  searchDelay={ 100 }
                  onSelectionCallback={ (selectedOptions) => handleUserChange(selectedOptions[0]) }
                />
              }
            </Card.Header>
            <Card.Section>
              {/* NEGATIVE TOTAL (Only for seller or when as admin filter) */}
              {/* If seller or filtered seller display adjustments reason(else do not display at all) */}
              { focusedSellerData && focusedSellerTotalIsNegative && 
                <>
                  <p>No hay abono en este corte ya que el monto es negativo</p> 
                  {/* {/* Sale credits (returns) OR Adjustments (from previous salereport negative total OR ManualPayment adjustments) */}
                  <div className='flex'>
                    <p className='mr5'><strong>Razón: </strong></p> 
                    <SubstractionReasons reasons={ focusedSellerData.substractions_reasons } />
                  </div>
                </>
              }

              {/* POSITIVE TOTAL */}
              {/* Positive total amount! 
                1. Total with adjustments ONLY no SC negatives (default for Seller, when filtered for Admin)
                2. Default positive total(default for admin unless filter)*/
              }
              { !focusedSellerTotalIsNegative &&
                <div>
                  { focusedSellerData && focusedSellerData.adjustments_origin_sale_report_ids ?
                    <div>
                      <DisplayText><strong>{ focusedSellerTotal }</strong></DisplayText>
                      <div className='flex'>
                        <p className='mr5'>
                          Ventas de <strong>{ focusedSellerSubtotal }</strong>, menos <strong>{ focusedSellerData.substractions_money }</strong> de ajuste, se abonan de la siguiente manera. ¿Por qué tengo un ajuste?
                        </p>
                        <Tooltip content={ <SubstractionReasons reasons={ focusedSellerData.substractions_reasons }/> }>
                          <Icon source={ QuestionMarkMajorMonotone } />
                        </Tooltip>
                      </div>
                    </div>
                  :
                    <div>
                      {/* Default view for Admin. Display saleReport.totals_money */}
                      <DisplayText size="medium"><strong>{ focusedSellerTotal || join(saleReport.totals_money, " y ") || 0 }</strong></DisplayText> 
                      <p>Se abonan de la siguiente manera.</p>
                    </div>
                      
                  }
                </div>
              }
            </Card.Section>

            <SaleReportTransfersBrief 
              isSeller={ props.isSeller } 
              transferURLBase={ props.transferURLBase } 
              reportData={ focusedSellerData ? [focusedSellerData] : transactionsReport.report_items } 
              skuTooltip={ I18n.t("dashboard.transfer.tooltip.payment_destination") }
            />
          </Card>
        </div>

        { focusedSellerData &&
          <Card title="Detalle de Ventas">
            <TransactionsHistory 
              isSeller={ props.isSeller } 
              transferURLBase={ props.transferURLBase } 
              transactions={ focusedSellerData.transaction_history } 
              transactionTotalsMoney={ focusedSellerData.transaction_totals_money }
              displayTransferColumn={ props.isSeller ? false : true }
            />
          </Card>
        }
      </Page>
    </AppProvider>
  )
}

const SubstractionReasons = ({ reasons }) => (
  <div>
    { reasons.map( (reason, index) => (
      <p key={ index }>{ reason }</p>  
    ))}
  </div>
)


SaleReportShow.propTypes = propTypes;


export default SaleReportShow;
